//this file is ProcessForm.jsx

import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { TextField, FormControl } from '@mui/material';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import TextInput from 'src/components/UI/input/TextInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import ConversionSuggestionCard from './ConversionSuggestionCard';
import {
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext, UserContext } from 'src/store/ContextStore';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';

// The fields an edit compares (TGCLOUD-1902), in screen order.
const PROCESS_FIELDS = [
  'serviceProvider',
  'processType',
  'processStatus',
  'processStartDate',
  'ageAtRecord',
  'endDate',
  'staffMember',
  'processComment'
];

export default function ProcessForm({
  data,
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  isDisabled,
  // How many fields hold an unsaved change: what closing it asks by.
  onChangeCount,
  // Handed to the conversion card only, so its "Try as a Tag" can put the tag
  // on the member's row as well as on the server.
  setMemberRecords
}) {
  const t = useAutoTranslation();

  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { user: loggedInUser } = React.useContext(UserContext);
  const { setSnack } = React.useContext(SnackContext);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const patientDob = data?.dob;
  // The age on the calendar day the process started (calculateAgeOnDate), as
  // the other clinical-record forms work theirs out. Nothing calculated it
  // before, so every Process was saved with no age at record. The start date
  // has two writers — this form's picker (a local-anchored instant) and the
  // spreadsheet / Desktop importer (processWriter: new Date('YYYY-MM-DD'), UTC
  // midnight) — and only the raw value says which, so it is read by
  // toLocalDateFromPickerOrImport. A start date before the birth gives no age.
  // The date fields show the day they read (asPickerDay): new Date() showed an
  // imported date a day early in New York, so the day on screen is the day the
  // age is from. The end date has the same two writers.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (dateValue) => calculateAgeOnDate(patientDob, dateValue),
    [patientDob]
  );

  const defaultValues = isEditing
    ? {
        ...model,
        staffMember: Array.isArray(model.staffMember) ? model.staffMember : []
      }
    : {
        familyId: user.familyId,
        familyMemberId,
        serviceProvider: '',
        processType: '',
        processStatus: '',
        processStartDate: null,
        endDate: null,
        ageAtRecord: '',
        processComment: '',
        familyNote: false,
        displayInPedigree: false,
        staffMember: loggedInUser?._id ? [loggedInUser._id] : []
      };

  const [formValues, setFormValues] = React.useState(defaultValues);
  const ageIsComputed = !!patientDob && !!formValues.processStartDate;
  // Unsaved changes (TGCLOUD-1902): each field against the record as opened.
  const changes = useChangedFields({
    baseline: defaultValues,
    values: formValues,
    mode: isEditing ? 'edit' : 'create',
    fields: PROCESS_FIELDS,
    exclude: (name) => name === 'ageAtRecord' && ageIsComputed
  });
  React.useEffect(() => {
    onChangeCount?.(changes.unsavedCount);
  }, [changes.unsavedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add-as-you-type option lists, persisted as clinical defaults (same
  // mechanism + permissions as the Appointment "type" field). New values are
  // creatable by admin / genetics / geneticsServiceAdmin via AutocompleteInput.
  const [serviceProviderOptions, setServiceProviderOptions] = React.useState(
    []
  );
  const [processTypeOptions, setProcessTypeOptions] = React.useState([]);
  const [processStatusOptions, setProcessStatusOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('serviceProviders').then(setServiceProviderOptions);
    getClinicalDefaults('processTypes').then(setProcessTypeOptions);
    getClinicalDefaults('processStatuses').then(setProcessStatusOptions);
  }, []);

  // Recomputed whenever the date of birth or the start date changes — on
  // opening a saved record too, as the sibling forms do, so a record imported
  // without an age, or saved before this was calculated, shows the age on its
  // start date. Not marked as an unsaved change: nothing the user did.
  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.processStartDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.processStartDate]);

  const handleChange = (e) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onToggleSwitch = (e) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const allStaffMembers = React.useMemo(
    () => staffMemberOptions.map((o) => o.value),
    [staffMemberOptions]
  );
  const staffMembersMap = React.useMemo(
    () =>
      staffMemberOptions.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    [staffMemberOptions]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Resolve selected staff userIds back to the stored {userId, firstName,
    // lastName} shape (same as the other clinical-note forms).
    let staffMember = [];
    if (
      Array.isArray(formValues.staffMember) &&
      formValues.staffMember.length
    ) {
      staffMember = formValues.staffMember
        .map((id) => staffMembers.find((m) => m.userId === id))
        .filter(Boolean)
        .map((m) => ({ ...m }));
    }

    const payload = {
      ...formValues,
      staffMember
    };

    return axios
      .put(APICONSTANTS.process, payload)
      .then(() => {
        handleClose({ exit: true });
        setSnack({
          status: true,
          msg: t('Successfully saved'),
          severity: 'success'
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Save failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  const deleteNote = () => {
    setLoading(true);
    axios
      .delete(`/clinical-notes/note`, { params: { _id: model._id } })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        setOpen(false);
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={() => setOpen(false)}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Process ?"
        content={t(
          'Warning: This action will permanently delete this Process. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      <Grid container spacing={2}>
        <ChangedField
          component={Grid}
          size={12}
          name="serviceProvider"
          changed={changes.changed.serviceProvider}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Service Provider</FormLabel>
          </Grid>
          <AutocompleteInput
            name="serviceProvider"
            value={formValues.serviceProvider || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, serviceProvider: label }));
              if (label) {
                saveClinicalDefault('serviceProviders', label).then(
                  setServiceProviderOptions
                );
              }
            }}
            options={serviceProviderOptions}
            onDelete={(option) => {
              deleteClinicalDefault('serviceProviders', option.label).then(
                setServiceProviderOptions
              );
            }}
            helperText={t('Please select or enter a service provider')}
            disabled={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="processType"
          changed={changes.changed.processType}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Type</FormLabel>
          </Grid>
          <AutocompleteInput
            name="processType"
            value={formValues.processType || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, processType: label }));
              if (label) {
                saveClinicalDefault('processTypes', label).then(
                  setProcessTypeOptions
                );
              }
            }}
            options={processTypeOptions}
            onDelete={(option) => {
              deleteClinicalDefault('processTypes', option.label).then(
                setProcessTypeOptions
              );
            }}
            helperText={t('Please select or enter a process type')}
            disabled={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="processStatus"
          changed={changes.changed.processStatus}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Status</FormLabel>
          </Grid>
          <AutocompleteInput
            name="processStatus"
            value={formValues.processStatus || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, processStatus: label }));
              if (label) {
                saveClinicalDefault('processStatuses', label).then(
                  setProcessStatusOptions
                );
              }
            }}
            options={processStatusOptions}
            onDelete={(option) => {
              deleteClinicalDefault('processStatuses', option.label).then(
                setProcessStatusOptions
              );
            }}
            helperText={t('Please select or enter a process status')}
            disabled={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="processStartDate"
          changed={changes.changed.processStartDate}
        >
          <TextInput
            type="date"
            label="Process Start Date"
            name="processStartDate"
            value={asPickerDay(formValues.processStartDate)}
            onChange={(e) => {
              setFormValues((prev) => ({
                ...prev,
                processStartDate: e,
                ageAtRecord: computeAgeAtRecord(e)
              }));
            }}
            minDate={new Date(1600, 0, 0)}
            readOnly={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="ageAtRecord"
          changed={changes.changed.ageAtRecord}
          disabled={ageIsComputed}
        >
          <TextInput
            label="Age at Process Start"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) when an age is actually being auto-calculated,
            // i.e. DoB AND start date both set, or when the form is read-only.
            // Editable when either date is missing so a known age can be
            // recorded directly.
            disabled={
              isDisabled || (!!patientDob && !!formValues.processStartDate)
            }
            onChange={
              isDisabled || (patientDob && formValues.processStartDate)
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                    }
                  }
            }
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="endDate"
          changed={changes.changed.endDate}
        >
          <TextInput
            type="date"
            label="End Date"
            name="endDate"
            value={asPickerDay(formValues.endDate)}
            onChange={(e) => {
              setFormValues((prev) => ({ ...prev, endDate: e }));
            }}
            minDate={new Date(1600, 0, 0)}
            readOnly={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={12}
          name="staffMember"
          changed={changes.changed.staffMember}
        >
          <FormControl component="fieldset" fullWidth>
            <TextInput
              label="Staff Member"
              name="staffMember"
              type="multiple-select"
              value={formValues.staffMember || []}
              onChange={handleChange}
              options={allStaffMembers || []}
              mapper={staffMembersMap || {}}
              disabled={isDisabled}
            />
          </FormControl>
        </ChangedField>

        <ChangedField
          component={Grid}
          size={12}
          name="processComment"
          changed={changes.changed.processComment}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Comment</FormLabel>
          </Grid>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            name="processComment"
            value={formValues.processComment || ''}
            onChange={handleChange}
            slotProps={{
              htmlInput: { readOnly: isDisabled }
            }}
          />
        </ChangedField>

        {/* The two flags. There is no Private switch: TGCLOUD-1766 retired the
            TrakGene Desktop private flag, so Cloud neither shows nor sets it. */}
        {[
          { name: 'familyNote', label: 'Family Note' },
          { name: 'displayInPedigree', label: 'Display in Pedigree' }
        ].map(({ name, label }) => (
          <Grid
            container
            key={name}
            size={12}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 6,
                sm: 7
              }}
            >
              <FormLabel component="label" className="form-label">
                {label}
              </FormLabel>
            </Grid>
            <Grid
              container
              size={{
                xs: 6,
                sm: 3
              }}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid>
                <Switch
                  color="secondary"
                  name={name}
                  checked={formValues[name] ?? false}
                  onChange={onToggleSwitch}
                  disabled={isDisabled}
                />
              </Grid>
              <Grid>
                <Typography>{formValues[name] ? t('Yes') : t('No')}</Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}

        {/* Opt-in conversion suggestion — appears on open/edit, never converts
            on save. Shown once a Process Type is present. */}
        {formValues.processType?.trim() && (
          <Grid size={12}>
            <ConversionSuggestionCard
              process={formValues}
              probandUserId={data?._id}
              data={data}
              user={user}
              setMemberRecords={setMemberRecords}
            />
          </Grid>
        )}

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={
            isEditing && !isDisabled ? () => setOpen(true) : null
          }
          addLabel={`Add new process`}
          unsavedCount={changes.count}
        />
      </Grid>
    </form>
  );
}
